/**
 * widgets/CameraScanner.ts · 摄像头 QR 扫码 v339 (P0 工人 SOP 60% 时间)
 *
 * 复用 v200 v176 TN_V176 设计:
 *   - html5-qrcode CDN 懒加载 (250KB, 仅扫码时加载)
 *   - 全屏黑底 modal · 360×360 取景框
 *   - facingMode: 'environment' 后置摄像头
 *   - onResult(text) 回调
 *
 * 用法:
 *   import { openCameraScanner } from '@widgets/CameraScanner';
 *   openCameraScanner({ onResult: (qrText) => { ... } });
 */

import { toast } from '@services/toast';

interface CameraOpts {
  onResult: (text: string) => void;
  title?: string;
}

let cdnPromise: Promise<any> | null = null;

function loadHtml5QrCode(): Promise<any> {
  if (cdnPromise) return cdnPromise;
  cdnPromise = new Promise((resolve, reject) => {
    if ((window as any).Html5Qrcode) { resolve((window as any).Html5Qrcode); return; }
    const s = document.createElement('script');
    s.src = 'https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js';
    s.onload = () => resolve((window as any).Html5Qrcode);
    s.onerror = () => reject(new Error('html5-qrcode CDN 加载失败'));
    document.head.appendChild(s);
  });
  return cdnPromise;
}

export async function openCameraScanner(opts: CameraOpts): Promise<void> {
  const title = opts.title || '📷 扫码';
  // Modal
  const backdrop = document.createElement('div');
  backdrop.id = 'tn-camera-backdrop';
  backdrop.style.cssText = 'position:fixed;inset:0;z-index:99998;background:rgba(0,0,0,0.95);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:20px;';
  backdrop.innerHTML = `
    <div style="color:white;font-size:18px;font-weight:600;margin-bottom:16px;text-align:center;">${title}</div>
    <div id="tn-qr-reader" style="width:min(360px,90vw);height:min(360px,90vw);background:#000;border-radius:8px;overflow:hidden;"></div>
    <div style="color:rgba(255,255,255,0.7);font-size:13px;margin-top:16px;text-align:center;max-width:340px;">将 QR 码对准取景框 · Đặt mã QR vào khung</div>
    <button id="tn-qr-cancel" style="margin-top:24px;padding:12px 32px;background:white;color:#333;border-radius:8px;font-weight:600;border:none;cursor:pointer;font-size:14px;">✕ 取消 · Hủy</button>
  `;
  document.body.appendChild(backdrop);

  let scanner: any = null;
  const close = () => {
    try { scanner?.stop?.()?.catch?.(() => {}); } catch {}
    backdrop.remove();
  };

  backdrop.querySelector<HTMLButtonElement>('#tn-qr-cancel')!.onclick = close;
  document.addEventListener('keydown', function esc(e) {
    if (e.key === 'Escape') { close(); document.removeEventListener('keydown', esc); }
  });

  try {
    const Html5Qrcode = await loadHtml5QrCode();
    scanner = new Html5Qrcode('tn-qr-reader');
    await scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 250, height: 250 } },
      (decodedText: string) => {
        opts.onResult(decodedText);
        close();
      },
      () => { /* qr not found in frame · ignore */ }
    );
  } catch (err) {
    toast.error('📷 摄像头启动失败: ' + (err instanceof Error ? err.message : String(err)));
    // 降级: prompt 手输
    const manual = prompt('摄像头不可用 · 请手输 QR 码内容:');
    close();
    if (manual) opts.onResult(manual);
  }
}
